import Enum from 'enum';
import { Map, List, fromJS } from 'immutable';
import moment from 'moment';

export const DurationState = new Enum({
    0: 'default',
    1: 'specific',
    2: 'weekday',
    3: 'monday',
});

/**
 * The lower the number the more eager it is to activate!
 */
export const PriorityItems = new Enum({
    25: 'HIGH_PRIORITY',
    50: 'STANDARD_PRIORITY',
    75: 'LOW_PRIORITY',
});

export const ParallelCallTypes = new Enum(
    {
        FIXED: 'fixed',
        MOBILE: 'mobile',
        BOTH: 'both',
    },
    { freez: true },
);

export const BusyTypes = new Enum(
    {
        BUSY: 'busy',
        VOICEMAIL: 'voicemail',
        NUMBER: 'number',
    },
    { freez: true },
);

export const DefaultDuration = new Enum(
    {
        DEFAULT: 'default',
        SPECIFIC: 'specific',
        WEEKDAY: 'weekday',
        MONDAY: 'monday',
    },
    { freez: true },
);

export const MAX_PAUSE_PROFILE_DURATION_IN_MINUTES = 30;

/**
 * If the profileKey is defined return the profile with the matching value.
 * Otherwise, return the profile after the one with plusId 0.
 */
export const getProfileFromProfileList = (profileKey, profiles) => {
    let profile;

    if (profileKey) {
        profile = profiles.find((profile) => profile.get('key') === profileKey);
    } else {
        profile = sortProfilesAfterPlusId(profiles).skip(1).first();
    }

    return profile;
};

export const getProfileDTOWithValue = (profile, criteria, value) => {
    const profileDTO = fromJS({ key: profile.get('key') });
    return profileDTO.setIn(criteria, value);
};

/**
 * Change All profile but the first for the wanted criteria
 *
 * @param profileList
 * @param criteria
 * @returns {Iterable<K, V>}
 */
export const updateProfileListByCriteria = (profileList, criteria, value) => {
    const profiles = profileList.skip(1);
    const updatedProfiles = profiles.map((profile) =>
        getProfileDTOWithValue(profile, criteria, value),
    );
    const firstProfile = profileList.take(1).first();
    const availableProfile = fromJS([{ key: firstProfile.get('key') }]);

    return availableProfile.concat(updatedProfiles);
};

export const getDefaultProfile = (i18next) =>
    fromJS({
        plusId: -1,
        description: i18next.t('status.unknown'),
        activeUntil: '1970-01-01T00:00',
    });

export const getDefaultDurationId = (defaultDuration) => {
    const parts = defaultDuration ? defaultDuration.split(';') : ['0'];

    const first = parseInt(parts[0]);

    switch (first) {
        case 0: {
            return 0;
        }
        case 1: {
            return 1;
        }
        case 2: {
            const type = parseInt(parts[1]);
            if (type === 0) {
                return 3;
            } else {
                return 2;
            }
        }
        default:
    }

    throw new Error(`Invalid default duration format: ${defaultDuration}`);
};

export const getDefaultDurationTextFromId = (profile, i18next) => {
    const defaultDuration = profile
        ? profile.get('defaultDuration')
        : '1;0;0;1;0'; // If there's no profile, configure the specific duration to last for one hour
    const seconds = profile ? profile.get('pauseDuration') : 0; //Pause duration in seconds only defined in pause profiles
    const id = getDefaultDurationId(defaultDuration);

    switch (id) {
        case 0: {
            return i18next.t('profilechooser.until_further_notice');
        }
        case 1: {
            return getDurationInTime(defaultDuration, seconds, i18next);
        }
        case 2: {
            return i18next.t('settings.profile.weekday');
        }
        case 3: {
            return i18next.t('settings.profile.monday');
        }
        default:
    }

    throw new Error(`Invalid default duration format: ${defaultDuration}`);
};

const getDurationInTime = (defaultDuration, seconds, i18next) => {
    const parts = defaultDuration.split(';');

    const weeks = parseInt(parts[1]);
    const days = parseInt(parts[2]);
    const hours = parseInt(parts[3]);
    const minutes = parseInt(parts[4]);

    let date = '';

    if (weeks > 0) {
        date += weeks + i18next.t('settings.profile.short_week') + ' ';
    }
    if (days > 0) {
        date += days + 'd ';
    }
    if (hours > 0) {
        date += hours + i18next.t('settings.profile.short_hour') + ' ';
    }
    if (minutes > 0) {
        date += minutes + 'm ';
    }
    if (seconds > 0) {
        date += seconds + 's';
    }
    return date;
};

/**
 * This is a re-implementation of
 * ProfileDAO#resolveDefaultDuration(AccountProfile, java.util.Date) from the
 * core project.
 *
 * If you find bugs here, please see if they are present upstream also.
 *
 * @param defaultDuration see ProfileDAO#resolveDefaultDuration(AccountProfile, java.util.Date)
 * @param from a moment-object (momentjs.com)
 * @returns a moment-object
 */
export const resolveDefaultDuration = (defaultDuration, from) => {
    if (!moment.isMoment(from)) {
        throw new Error('Illegal argument: from is not a moment-object');
    }
    /*
    This function differs from resolveDefaultDuration here. There, defaultDuration is not nullable, but in this
    method it is. Hence the null guard ['0'].
     */
    const parts = defaultDuration ? defaultDuration.split(';') : ['0'];

    const first = parseInt(parts[0]);

    const dt = from.clone();
    switch (first) {
        case 0: {
            return null;
        }
        case 1: {
            const weeks = parseInt(parts[1]);
            const days = parseInt(parts[2]);
            const hours = parseInt(parts[3]);
            const minutes = parseInt(parts[4]);
            return dt
                .add(weeks, 'weeks')
                .add(days, 'days')
                .add(hours, 'hours')
                .add(minutes, 'minutes');
        }
        case 2: {
            const type = parseInt(parts[1]);
            if (type === 0) {
                dt.isoWeekday(8);
            } else if (type === 1) {
                if (dt.isoWeekday() > 4) {
                    dt.isoWeekday(8);
                } else {
                    dt.add(1, 'days');
                }
            }
            const hours = parseInt(parts[2]);
            const minutes = parseInt(parts[3]);
            return dt.hour(hours).minute(minutes);
        }
        default:
    }

    throw new Error(
        "Illegal argument: Illegal defaultDuration format: '" +
            defaultDuration +
            "'",
    );
};

/**
 *  Get value if a user is logged in to a queue or not for a specific profileDTO
 *
 * @param profile a profileDTO
 * @param queueKey the queue to check the value for
 * @return map with the logged in status for a profile.
 */
export const getQueueLoggedInValueForProfile = (profile, queueKey) => {
    return profile
        .get('queueLoginList', Map())
        .find(
            (findQueueLogin) =>
                findQueueLogin.get('queueEntityKey') === queueKey,
            this,
            Map(),
        )
        .get('loggedIn', false);
};

export const getChannelLoggedInValueForProfile = (profile, channelKey) => {
    return profile
        .get('channelLoginList', Map())
        .find(
            (login) => login.get('channelEntityKey') === channelKey,
            this,
            Map(),
        )
        .get('loggedIn', false);
};

export const sortProfilesAfterPlusId = (profiles) => {
    if (!profiles) {
        return null;
    }
    return profiles.sortBy((profile) => profile.get('plusId'));
};

export const updateFixedProfile = (profile, profileCallerId) => {
    const updatedProfile = fromJS({ key: profile.get('key') });

    if (
        profileCallerId === 'fixed' ||
        profileCallerId === 'msisdn' ||
        profileCallerId === 'hidden'
    ) {
        return updatedProfile.setIn(
            ['callerId', 'fixedCallerIdType'],
            profileCallerId,
        );
    } else {
        return updatedProfile
            .setIn(['callerId', 'fixedCallerIdType'], 'mainanumber')
            .setIn(['callerId', 'fixedMainNumber', 'key'], profileCallerId);
    }
};

export const updateMobileProfile = (profile, profileCallerId) => {
    const updatedProfile = fromJS({ key: profile.get('key') });

    if (
        profileCallerId === 'fixed' ||
        profileCallerId === 'msisdn' ||
        profileCallerId === 'hidden'
    ) {
        return updatedProfile.setIn(
            ['callerId', 'mexCallerIdType'],
            profileCallerId,
        );
    } else {
        return updatedProfile
            .setIn(['callerId', 'mexCallerIdType'], 'mainanumber')
            .setIn(['callerId', 'mexMainNumber', 'key'], profileCallerId);
    }
};

export const updateSMSSenderName = (profile, key) => {
    const updatedProfile = fromJS({ key: profile.get('key') });

    return updatedProfile.setIn(['smsSenderNameId', 'key'], key);
};

export const getProfileCallerId = (
    i18next,
    profile,
    contact,
    callerIdType,
    mainNumberType,
) => {
    const profileCallerId = profile.getIn(['callerId', callerIdType]);
    switch (profileCallerId) {
        case 'mainanumber':
            return profile.getIn([
                'callerId',
                mainNumberType,
                'number',
                'nationalFormat',
            ]);
        case 'fixed':
            return contact.getIn(['fixed', 'nationalFormat']);
        case 'msisdn':
            return contact.getIn(['mobile', 'nationalFormat']);
        case 'hidden':
            return i18next.t('settings.hidden_number');
        default:
            return '';
    }
};

export const hasLandlineCallerId = (profile) =>
    profile?.getIn(['callerId', 'hasLandlineCapableEndpoint']) !== false;

export const createProfileWithAfterReferalSetting = (
    profileKey,
    value,
    numberDTO = undefined,
) => {
    let updated = fromJS({ key: profileKey });

    if (value === 'voicemail') {
        updated = updated
            .setIn(['callForwarding', 'unconditionalRedirect', 'type'], value)
            .setIn(['spokenNotification', 'referActionType'], 'noop');
    } else if (value === 'number') {
        updated = updated
            .setIn(['callForwarding', 'unconditionalRedirect', 'type'], value)
            .setIn(
                ['callForwarding', 'unconditionalRedirect', 'forwardingNumber'],
                numberDTO,
            )
            .setIn(['spokenNotification', 'referActionType'], 'noop');
    } else {
        updated = updated.setIn(
            ['callForwarding', 'unconditionalRedirect', 'type'],
            'busy',
        );
        if (value === 'loop') {
            updated = updated.setIn(
                ['spokenNotification', 'referActionType'],
                value,
            );
        } else if (value === 'hangup') {
            updated = updated.setIn(
                ['spokenNotification', 'referActionType'],
                value,
            );
        } else {
            updated = updated.setIn(
                ['spokenNotification', 'referActionType'],
                'noop',
            );
            updated = updated.setIn(
                ['parallelCalls', 'parallelCallType'],
                value,
            );
        }
    }

    return updated;
};

/* Special checkers for checking nested caller id values */
export const callerIdLandlineVaries = (profileList) => {
    const landlineDataGetter = (profile) => {
        const callerIdType = profile.getIn(['callerId', 'fixedCallerIdType']);
        return callerIdType === 'mainanumber'
            ? profile.getIn(['callerId', 'fixedMainNumber', 'key'])
            : callerIdType;
    };
    return callerIdVaries(profileList, landlineDataGetter);
};

export const callerIdMobileVaries = (profileList) => {
    const mobileDataGetter = (profile) => {
        const callerIdType = profile.getIn(['callerId', 'mexCallerIdType']);
        return callerIdType === 'mainanumber'
            ? profile.getIn(['callerId', 'mexMainNumber', 'key'])
            : callerIdType;
    };
    return callerIdVaries(profileList, mobileDataGetter);
};

export const callerIdTextMessageVaries = (profileList) => {
    const textMessageDataGetter = (profile) =>
        profile.getIn(['smsSenderNameId', 'key']);
    return callerIdVaries(profileList, textMessageDataGetter);
};
/* */

/* Default checkers for varied states */
export const callerIdVaries = (profileList, providedDataGetter) => {
    const referenceProfile = profileList && profileList.first();
    const dataGetter = providedDataGetter || callerIdDataGetter;

    return profileSettingsVary(referenceProfile, profileList, dataGetter);
};

export const incomingCallsVaries = (profileList) => {
    if (!profileList || profileList.isEmpty() || profileList.size === 1) {
        return false;
    }
    const profileListWithoutAvailable = profileList.skip(1);
    const referenceProfile = sortProfilesAfterPlusId(
        profileListWithoutAvailable,
    ).first();
    return profileSettingsVary(
        referenceProfile,
        profileListWithoutAvailable,
        incomingCallsDataGetter,
    );
};

export const callRecordingVaries = (profileList) => {
    const referenceProfile = profileList && profileList.first();
    return profileSettingsVary(
        referenceProfile,
        profileList,
        callRecordingDataGetter,
    );
};

export const personalCallRecordingVaries = (profileList) => {
    return profileSettingsVary(
        profileList?.first(),
        profileList,
        personalCallRecordingDataGetter,
    );
};

export const monitoringVaries = (profileList) =>
    profileSettingsVary(
        profileList?.first(),
        profileList,
        monitoringDataGetter,
    );

export const queueLoginVaries = (profileList) => {
    const referenceProfile = profileList && profileList.first();
    return profileSettingsVary(
        referenceProfile,
        profileList,
        queueLoginDataGetter,
    );
};

export const channelLoginVaries = (profileList) => {
    const referenceProfile = profileList && profileList.first();
    return profileSettingsVary(
        referenceProfile,
        profileList,
        channelLoginDataGetter,
    );
};
/* */

/* The DataGetters describes how to extract the data that should be compared from a single profile */
const callerIdDataGetter = (profile) => {
    const landlineCallerIdType = profile.getIn([
        'callerId',
        'fixedCallerIdType',
    ]);
    const mobileCallerIdType = profile.getIn(['callerId', 'mexCallerIdType']);
    return Map()
        .set(
            'landline',
            landlineCallerIdType === 'mainanumber'
                ? profile.getIn(['callerId', 'fixedMainNumber', 'key'])
                : landlineCallerIdType,
        )
        .set(
            'mobile',
            mobileCallerIdType === 'mainanumber'
                ? profile.getIn(['callerId', 'mexMainNumber', 'key'])
                : mobileCallerIdType,
        )
        .set('text', profile.getIn(['smsSenderNameId', 'key']));
};

const incomingCallsDataGetter = (profile) => {
    return Map()
        .set('parallelCalls', profile.get('parallelCalls').delete('groupId'))
        .set('callForwarding', profile.get('callForwarding').delete('groupId'))
        .set(
            'spokenNotification',
            profile
                .get('spokenNotification')
                .delete('groupId')
                .delete('referEntry'),
        )
        .set('callMVoip', profile.get('callMVoip'));
};

const callRecordingDataGetter = (profile) => {
    let result = Map();

    if (profile.has('recordCalls')) {
        result = result.set('recordCalls', profile.get('recordCalls'));
    }

    if (profile.has('autoTranscribe')) {
        result = result.set('autoTranscribe', profile.get('autoTranscribe'));
    }

    return result;
};

const personalCallRecordingDataGetter = (profile) => {
    let result = Map();

    if (profile.has('personalRecordCalls')) {
        result = result.set(
            'personalRecordCalls',
            profile.get('personalRecordCalls'),
        );
    }

    if (profile.has('personalAutoTranscribe')) {
        result = result.set(
            'personalAutoTranscribe',
            profile.get('personalAutoTranscribe'),
        );
    }

    return result;
};

const monitoringDataGetter = (profile) =>
    Map().set('canBeCoMonitored', profile.get('canBeCoMonitored'));

const queueLoginDataGetter = (profile) => {
    if (!profile.get('queueLoginList')) {
        return Map();
    }
    return profile
        .get('queueLoginList')
        .reduce(
            (acc, queueLogin) =>
                acc.set(
                    queueLogin.get('queueEntityKey'),
                    queueLogin.get('loggedIn'),
                ),
            Map(),
        );
};

const channelLoginDataGetter = (profile) => {
    if (!profile.get('channelLoginList')) {
        return Map();
    }
    return profile
        .get('channelLoginList')
        .reduce(
            (acc, channelLogin) =>
                acc.set(
                    channelLogin.get('channelEntityKey'),
                    channelLogin.get('loggedIn'),
                ),
            Map(),
        );
};
/* */

const profileSettingsVary = (
    referenceProfile,
    profileList,
    dataGetterFunction,
) => {
    if (!referenceProfile) {
        return false;
    }
    const referenceData = dataGetterFunction(referenceProfile);

    return profileList.some((profile) => {
        const dynamicProfileData = dataGetterFunction(profile);
        const isSame = dataIsSame(referenceData, dynamicProfileData);
        return !isSame;
    });
};

const dataIsSame = (referenceData, dynamicData) => {
    if (referenceData === dynamicData) {
        return true;
    } else if (List.isList(referenceData) || Map.isMap(referenceData)) {
        return referenceData.equals(dynamicData);
    } else {
        return false;
    }
};

const PROFILE_DESCRIPTION_NORMAL = 'profile.standard.normal';
const PROFILE_DESCRIPTION_AVAILABLE = 'profile.standard.available';
const PROFILE_DESCRIPTION_LUNCH = 'profile.standard.lunch';
const PROFILE_DESCRIPTION_LEFTFORTHEDAY = 'profile.standard.leftfortheday';
const PROFILE_DESCRIPTION_MEETING = 'profile.standard.meeting';
const PROFILE_DESCRIPTION_VACATION = 'profile.standard.vacation';
const PROFILE_DESCRIPTION_SICKLEAVE = 'profile.standard.sickleave';
const PROFILE_DESCRIPTION_TEMPOUT = 'profile.standard.tempout';
const PROFILE_DESCRIPTION_BUSINESSTRIP = 'profile.standard.businesstrip';
const PROFILE_DESCRIPTION_SICKKID = 'profile.standard.sickkid';
const PROFILE_DESCRIPTION_EDUCATION = 'profile.standard.education';
const PROFILE_DESCRIPTION_BUSY = 'profile.standard.busy';
const PROFILE_DESCRIPTION_DONOTDISTURB = 'profile.standard.donotdisturb';
const QUEUE_STATUS_OPEN = 'pbx.open';
const QUEUE_STATUS_CLOSED = 'pbx.closed';

/**
 * Special characters in unicode:
 * u{00e4} = ä
 * u{00e5} = å
 * u{00e6} = æ
 * u{00f6} = ö
 * u{00f8} = ø
 * u{00f0} = ð
 * u{00ed} = í
 * u{00fa} = ú
 * u{00e1} = á
 * u{00f3} = ó
 * u{00D6} = Ö
 * u{00C5} = Å
 */

const standardProfileDescriptionTranslationKeysMap = Map({
    Normal: PROFILE_DESCRIPTION_NORMAL,
    Normaali: PROFILE_DESCRIPTION_NORMAL,
    Vanligt: PROFILE_DESCRIPTION_NORMAL,

    Tillgänglig: PROFILE_DESCRIPTION_AVAILABLE,
    Available: PROFILE_DESCRIPTION_AVAILABLE,
    Tilgjengelig: PROFILE_DESCRIPTION_AVAILABLE,
    Tilgængelig: PROFILE_DESCRIPTION_AVAILABLE,
    Käytettävissä: PROFILE_DESCRIPTION_AVAILABLE,
    'T\u{00f8}k/ur': PROFILE_DESCRIPTION_AVAILABLE,
    Disponible: PROFILE_DESCRIPTION_AVAILABLE,

    Lunch: PROFILE_DESCRIPTION_LUNCH,
    Lunsj: PROFILE_DESCRIPTION_LUNCH,
    Frokost: PROFILE_DESCRIPTION_LUNCH,
    Lounas: PROFILE_DESCRIPTION_LUNCH,
    Døgurði: PROFILE_DESCRIPTION_LUNCH,
    Esmorzar: PROFILE_DESCRIPTION_LUNCH,

    'Slutat f\u{00F6}r dagen': PROFILE_DESCRIPTION_LEFTFORTHEDAY,
    'Left for the day': PROFILE_DESCRIPTION_LEFTFORTHEDAY,
    'Sluttet for dagen': PROFILE_DESCRIPTION_LEFTFORTHEDAY,
    'G\u{00e5}et hjem': PROFILE_DESCRIPTION_LEFTFORTHEDAY,
    'L\u{00e4}htenyt kotiin': PROFILE_DESCRIPTION_LEFTFORTHEDAY,
    'Farin fyri dagin': PROFILE_DESCRIPTION_LEFTFORTHEDAY,
    'Ha plegat pel que queda de dia': PROFILE_DESCRIPTION_LEFTFORTHEDAY,

    Möte: PROFILE_DESCRIPTION_MEETING,
    Meeting: PROFILE_DESCRIPTION_MEETING,
    Møte: PROFILE_DESCRIPTION_MEETING,
    Møde: PROFILE_DESCRIPTION_MEETING,
    Kokous: PROFILE_DESCRIPTION_MEETING,
    Fundur: PROFILE_DESCRIPTION_MEETING,
    Reunió: PROFILE_DESCRIPTION_MEETING,

    Semester: PROFILE_DESCRIPTION_VACATION,
    Holiday: PROFILE_DESCRIPTION_VACATION,
    Ferie: PROFILE_DESCRIPTION_VACATION,
    Loma: PROFILE_DESCRIPTION_VACATION,
    'Farin \u{00ed} fr\u{00ed}': PROFILE_DESCRIPTION_VACATION,
    Vacances: PROFILE_DESCRIPTION_VACATION,

    Sjuk: PROFILE_DESCRIPTION_SICKLEAVE,
    'Sick leave': PROFILE_DESCRIPTION_SICKLEAVE,
    Syk: PROFILE_DESCRIPTION_SICKLEAVE,
    Syg: PROFILE_DESCRIPTION_SICKLEAVE,
    Sairas: PROFILE_DESCRIPTION_SICKLEAVE,
    Sjúkrafarloyvi: PROFILE_DESCRIPTION_SICKLEAVE,
    'De baixa per malaltia': PROFILE_DESCRIPTION_SICKLEAVE,

    'Tillf\u{00e4}lligt ute': PROFILE_DESCRIPTION_TEMPOUT,
    'Out of office': PROFILE_DESCRIPTION_TEMPOUT,
    'Ikke p\u{00e5} kontoret': PROFILE_DESCRIPTION_TEMPOUT,
    'Ude af kontoret': PROFILE_DESCRIPTION_TEMPOUT,
    'Tilap\u{00e4}isesti poissa': PROFILE_DESCRIPTION_TEMPOUT,
    'Ikki \u{00e1} skrivstovuni': PROFILE_DESCRIPTION_TEMPOUT,
    "Fora de l'oficina": PROFILE_DESCRIPTION_TEMPOUT,

    Tjänsteresa: PROFILE_DESCRIPTION_BUSINESSTRIP,
    'Business trip': PROFILE_DESCRIPTION_BUSINESSTRIP,
    Forretningsreise: PROFILE_DESCRIPTION_BUSINESSTRIP,
    Forretningsrejse: PROFILE_DESCRIPTION_BUSINESSTRIP,
    Työmatkalla: PROFILE_DESCRIPTION_BUSINESSTRIP,
    Arbeiðsferð: PROFILE_DESCRIPTION_BUSINESSTRIP,
    'Viatge de negocis': PROFILE_DESCRIPTION_BUSINESSTRIP,

    'V\u{00e5}rd av barn': PROFILE_DESCRIPTION_SICKKID,
    'Home with sick child': PROFILE_DESCRIPTION_SICKKID,
    'Hjemme med sykt barn': PROFILE_DESCRIPTION_SICKKID,
    'Barns sygedag': PROFILE_DESCRIPTION_SICKKID,
    'Kotona lapsen kanssa': PROFILE_DESCRIPTION_SICKKID,
    'Heima vi\u{00f0} sj\u{00fa}kum barni': PROFILE_DESCRIPTION_SICKKID,
    'A casa amb un nen malalt': PROFILE_DESCRIPTION_SICKKID,

    Utbildning: PROFILE_DESCRIPTION_EDUCATION,
    Training: PROFILE_DESCRIPTION_EDUCATION,
    Utdannelse: PROFILE_DESCRIPTION_EDUCATION,
    Kursus: PROFILE_DESCRIPTION_EDUCATION,
    Koulutus: PROFILE_DESCRIPTION_EDUCATION,
    Skeið: PROFILE_DESCRIPTION_EDUCATION,
    Formació: PROFILE_DESCRIPTION_EDUCATION,

    Upptagen: PROFILE_DESCRIPTION_BUSY,
    Busy: PROFILE_DESCRIPTION_BUSY,
    Opptatt: PROFILE_DESCRIPTION_BUSY,
    Optaget: PROFILE_DESCRIPTION_BUSY,
    Kiireinen: PROFILE_DESCRIPTION_BUSY,
    Upptikin: PROFILE_DESCRIPTION_BUSY,
    Ocupat: PROFILE_DESCRIPTION_BUSY,

    'St\u{00F6}r ej': PROFILE_DESCRIPTION_DONOTDISTURB,
    'Do not disturb': PROFILE_DESCRIPTION_DONOTDISTURB,
    'Ikke forstyrr': PROFILE_DESCRIPTION_DONOTDISTURB,
    'Forstyr ikke': PROFILE_DESCRIPTION_DONOTDISTURB,
    '\u{00c4}l\u{00e4} h\u{00e4}iritse': PROFILE_DESCRIPTION_DONOTDISTURB,
    'Ikki \u{00f3}r\u{00f3}gva': PROFILE_DESCRIPTION_DONOTDISTURB,
    'No molesteu': PROFILE_DESCRIPTION_DONOTDISTURB,

    Öppen: QUEUE_STATUS_OPEN,
    Open: QUEUE_STATUS_OPEN,
    Åpen: QUEUE_STATUS_OPEN,
    Åbent: QUEUE_STATUS_OPEN,
    Avoin: QUEUE_STATUS_OPEN,
    Opið: QUEUE_STATUS_OPEN,
    Obert: QUEUE_STATUS_OPEN,

    Stängd: QUEUE_STATUS_CLOSED,
    Closed: QUEUE_STATUS_CLOSED,
    Stengt: QUEUE_STATUS_CLOSED,
    Lukket: QUEUE_STATUS_CLOSED,
    Suljettu: QUEUE_STATUS_CLOSED,
    Stongt: QUEUE_STATUS_CLOSED,
    Tancades: QUEUE_STATUS_CLOSED,
});

export const getStandardProfileDescriptionInUserLanguage = (
    description,
    i18next,
) => {
    const translationKey =
        standardProfileDescriptionTranslationKeysMap.get(description);
    const descriptionPossiblyTranslated = translationKey
        ? i18next.t(translationKey)
        : description;

    return descriptionPossiblyTranslated;
};

export const canEditProfiles = (profileDTOList) => {
    return (
        profileDTOList &&
        profileDTOList.some((profile) => profile.get('editable') === true)
    );
};

export const hasPermissionToProfile = (profileDTOList, profileKey) => {
    return profileDTOList.some((profile) => profile.has(profileKey));
};

export const hasSpokenReferralActiveOnProfile = (profile) =>
    !!profile?.getIn(['spokenNotification', 'referEntry', 'key']);
